/* =========================================================
   Measurement Recommender
   File: assets/measurement-recommender.js
   ========================================================= */

(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-measurement-root]';
  const OPEN_SELECTOR = '[data-measurement-open]';
  const STORAGE_PREFIX = 'ftl_measurement_profile_v1:';
  const TOTAL_STEPS = 5;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const safeJSON = (script) => {
    if (!script) return null;

    try {
      const value = JSON.parse(script.textContent || 'null');

      // Shopify JSON metafields can sometimes arrive as a JSON string.
      if (typeof value === 'string') {
        try {
          return JSON.parse(value);
        } catch (_) {
          return value;
        }
      }

      return value;
    } catch (_) {
      return null;
    }
  };

  const dispatchNativeChange = (element) => {
    if (!element) return;

    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
  };

  class MeasurementRecommender {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('[data-measurement-panel]');
      this.form = root.querySelector('[data-measurement-form]');
      this.steps = [...root.querySelectorAll('[data-measurement-step]')];
      this.progress = root.querySelector('[data-measurement-progress]');
      this.closeButton = root.querySelector('[data-measurement-close]');
      this.backButton = root.querySelector('[data-measurement-back]');

      this.productId = root.dataset.productId || '';
      this.productHandle = root.dataset.productHandle || '';
      this.guideType = root.dataset.guideType || '';
      this.customerId = root.dataset.customerId || '';

      const container = root.parentElement || document;

      this.sizeChart =
        safeJSON(container.querySelector('[data-measurement-size-chart]')) ??
        safeJSON(document.querySelector('[data-measurement-size-chart]'));

      this.variants =
        safeJSON(container.querySelector('[data-measurement-product-variants]')) ??
        safeJSON(document.querySelector('[data-measurement-product-variants]')) ??
        [];

      this.currentStep = 1;
      this.lastFocusedElement = null;
      this.recommendedSize = null;
      this.isOpen = false;

      this.storageKey = `${STORAGE_PREFIX}${this.customerId || 'guest'}:${this.productHandle || this.productId || 'product'}`;

      this.bindEvents();
      this.setupWheels();
      this.setupUnitSwitchers();
      this.setupDropdowns();
      this.setupFitControl();
      this.setupBodyShapes();
      this.restoreProfile();
      this.showStep(1, false);
    }

    bindEvents() {
      this.closeButton?.addEventListener('click', () => this.close());
      this.backButton?.addEventListener('click', () => this.previousStep());

      this.root.addEventListener('click', (event) => {
        if (event.target === this.root) {
          this.close();
        }
      });

      this.root.querySelectorAll('[data-measurement-next]').forEach((button) => {
        button.addEventListener('click', () => this.nextStep());
      });

      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.saveProfile();
        this.close();
      });

      const applyButton = this.root.querySelector('[data-measurement-apply-size]');
      applyButton?.addEventListener('click', () => this.applyRecommendedSize());

      document.addEventListener('keydown', (event) => {
        if (!this.isOpen) return;

        if (event.key === 'Escape') {
          this.close();
        }

        if (event.key === 'Tab') {
          this.trapFocus(event);
        }
      });
    }

    open(trigger = null) {
      if (this.isOpen) return;

      this.lastFocusedElement = trigger || document.activeElement;
      this.isOpen = true;

      this.root.classList.add('is-open');
      this.root.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('measurement-recommender-lock');
      document.body.classList.add('measurement-recommender-lock');

      this.showStep(1, false);

      requestAnimationFrame(() => {
        this.panel?.focus();
      });
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;

      this.root.classList.remove('is-open');
      this.root.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('measurement-recommender-lock');
      document.body.classList.remove('measurement-recommender-lock');

      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus();
      }
    }

    showStep(stepNumber, focusPanel = true) {
      this.currentStep = clamp(Number(stepNumber) || 1, 1, TOTAL_STEPS);

      this.steps.forEach((step) => {
        const active = Number(step.dataset.measurementStep) === this.currentStep;

        step.hidden = !active;
        step.classList.toggle('is-active', active);
      });

      if (this.backButton) {
        this.backButton.hidden = this.currentStep === 1;
      }

      if (this.closeButton) {
        this.closeButton.hidden = this.currentStep !== 1;
      }

      if (this.progress) {
        this.progress.style.width = `${(this.currentStep / TOTAL_STEPS) * 100}%`;
      }

      if (this.currentStep === TOTAL_STEPS) {
        this.updateRecommendation();
      }

      if (focusPanel) {
        this.panel?.focus();
      }

      if (this.panel) {
        this.panel.scrollTop = 0;
      }
    }

    nextStep() {
      if (this.currentStep === 1 && !this.validateStepOne()) {
        return;
      }

      this.saveProfile();

      if (this.currentStep < TOTAL_STEPS) {
        this.showStep(this.currentStep + 1);
      }
    }

    previousStep() {
      if (this.currentStep > 1) {
        this.showStep(this.currentStep - 1);
      }
    }

    validateStepOne() {
      const input = this.form?.querySelector('input[name="name"]');
      const error = this.root.querySelector('[data-measurement-name-error]');

      if (!input || !error) return true;

      const value = input.value.trim();

      if (!value) {
        error.textContent = 'Please enter your name.';
        error.hidden = false;
        input.focus();
        return false;
      }

      error.hidden = true;
      error.textContent = '';
      return true;
    }

    setupDropdowns() {
      this.root.querySelectorAll('[data-measurement-dropdown]').forEach((dropdown) => {
        const trigger = dropdown.querySelector('[data-measurement-dropdown-trigger]');
        const menu = dropdown.querySelector('[data-measurement-dropdown-menu]');
        const display = dropdown.querySelector('[data-measurement-dropdown-value]');
        const input = dropdown.querySelector('[data-measurement-dropdown-input]');

        if (!trigger || !menu || !display || !input) return;

        trigger.addEventListener('click', () => {
          const expanded = trigger.getAttribute('aria-expanded') === 'true';

          trigger.setAttribute('aria-expanded', String(!expanded));
          menu.hidden = expanded;
        });

        menu.querySelectorAll('button[data-value]').forEach((option) => {
          option.addEventListener('click', () => {
            const value = option.dataset.value || option.textContent.trim();

            display.textContent = value;
            input.value = value;
            trigger.setAttribute('aria-expanded', 'false');
            menu.hidden = true;

            dispatchNativeChange(input);
          });
        });

        document.addEventListener('click', (event) => {
          if (!dropdown.contains(event.target)) {
            trigger.setAttribute('aria-expanded', 'false');
            menu.hidden = true;
          }
        });
      });
    }

    setupWheels() {
      this.root.querySelectorAll('[data-measurement-wheel]').forEach((wheel) => {
        const prev = wheel.querySelector('[data-wheel-prev]');
        const current = wheel.querySelector('[data-wheel-current]');
        const next = wheel.querySelector('[data-wheel-next]');
        const input = wheel.querySelector('[data-wheel-input]');

        if (!prev || !current || !next || !input) return;

        const setValue = (newValue) => {
          const min = Number(wheel.dataset.wheelMin);
          const max = Number(wheel.dataset.wheelMax);
          const value = clamp(Number(newValue), min, max);

          wheel.dataset.wheelValue = String(value);
          input.value = String(value);

          current.textContent = this.formatWheelValue(wheel, value);
          prev.textContent = this.formatWheelValue(wheel, clamp(value - 1, min, max));
          next.textContent = this.formatWheelValue(wheel, clamp(value + 1, min, max));

          prev.disabled = value <= min;
          next.disabled = value >= max;

          dispatchNativeChange(input);
        };

        wheel._setValue = setValue;

        prev.addEventListener('click', () => {
          setValue(Number(wheel.dataset.wheelValue) - 1);
        });

        next.addEventListener('click', () => {
          setValue(Number(wheel.dataset.wheelValue) + 1);
        });

        wheel.addEventListener(
          'wheel',
          (event) => {
            event.preventDefault();

            const direction = event.deltaY > 0 ? 1 : -1;
            setValue(Number(wheel.dataset.wheelValue) + direction);
          },
          { passive: false }
        );

        let touchStartY = null;

        wheel.addEventListener(
          'touchstart',
          (event) => {
            touchStartY = event.touches?.[0]?.clientY ?? null;
          },
          { passive: true }
        );

        wheel.addEventListener(
          'touchend',
          (event) => {
            if (touchStartY === null) return;

            const endY = event.changedTouches?.[0]?.clientY ?? touchStartY;
            const delta = touchStartY - endY;

            if (Math.abs(delta) > 22) {
              setValue(Number(wheel.dataset.wheelValue) + (delta > 0 ? 1 : -1));
            }

            touchStartY = null;
          },
          { passive: true }
        );

        setValue(Number(wheel.dataset.wheelValue));
      });
    }

    formatWheelValue(wheel, value) {
      const name = wheel.dataset.wheelName;

      if (name === 'height') {
        const unit = this.form?.querySelector('input[name="height_unit"]')?.value || 'cm';

        if (unit === 'in') {
          return Math.round(value / 2.54);
        }
      }

      if (name === 'weight') {
        const unit = this.form?.querySelector('input[name="weight_unit"]')?.value || 'kg';

        if (unit === 'lbs') {
          return Math.round(value * 2.2046226218);
        }
      }

      return String(Math.round(value));
    }

    refreshWheel(wheel) {
      if (!wheel || typeof wheel._setValue !== 'function') return;

      wheel._setValue(Number(wheel.dataset.wheelValue));
    }

    setupUnitSwitchers() {
      this.root.querySelectorAll('[data-unit-switcher]').forEach((switcher) => {
        const input = switcher.querySelector('[data-unit-input]');
        const buttons = [...switcher.querySelectorAll('[data-unit-value]')];

        if (!input || !buttons.length) return;

        buttons.forEach((button) => {
          button.addEventListener('click', () => {
            const value = button.dataset.unitValue;

            input.value = value;

            buttons.forEach((candidate) => {
              candidate.classList.toggle('is-active', candidate === button);
            });

            const dualWheel = switcher.closest('.measurement-recommender__dual-wheel');
            const wheel = dualWheel?.querySelector('[data-measurement-wheel]');

            this.refreshWheel(wheel);
            dispatchNativeChange(input);
          });
        });
      });
    }

    setupFitControl() {
      const range = this.root.querySelector('[data-measurement-fit-range]');
      const input = this.root.querySelector('[data-measurement-fit-input]');
      const buttons = [...this.root.querySelectorAll('[data-fit-value]')];

      if (!range || !input || !buttons.length) return;

      const labels = ['Fitted', 'Normal', 'Loose'];

      const setFit = (index) => {
        const value = clamp(Number(index), 0, 2);

        range.value = String(value);
        input.value = labels[value];

        buttons.forEach((button) => {
          button.classList.toggle('is-active', Number(button.dataset.fitValue) === value);
        });

        dispatchNativeChange(input);
      };

      range.addEventListener('input', () => setFit(range.value));

      buttons.forEach((button) => {
        button.addEventListener('click', () => setFit(button.dataset.fitValue));
      });

      setFit(range.value);
    }

    setupBodyShapes() {
      const groups = [
        { name: 'abdomen_shape', imageKey: 'abdomen' },
        { name: 'chest_shape', imageKey: 'chest' },
        { name: 'waist_shape', imageKey: 'waist' },
        { name: 'hip_shape', imageKey: 'hip' }
      ];

      const setImageForValue = (imageKey, value = '') => {
        const image = this.root.querySelector(
          `[data-measurement-shape-image="${imageKey}"]`
        );

        if (!image) return;

        const normalizedValue = String(value || '').trim().toLowerCase();
        const dataKey = normalizedValue
          ? `${normalizedValue}Src`
          : 'defaultSrc';

        const nextSrc =
          image.dataset[dataKey] ||
          image.dataset.defaultSrc ||
          image.getAttribute('src');

        if (!nextSrc || image.getAttribute('src') === nextSrc) return;

        image.classList.add('is-changing');

        const preload = new Image();

        preload.onload = () => {
          image.src = nextSrc;

          requestAnimationFrame(() => {
            image.classList.remove('is-changing');
          });
        };

        preload.onerror = () => {
          image.src = nextSrc;
          image.classList.remove('is-changing');
        };

        preload.src = nextSrc;
      };

      groups.forEach(({ name, imageKey }) => {
        const inputs = [
          ...this.root.querySelectorAll(`input[name="${name}"]`)
        ];

        if (!inputs.length) return;

        const image = this.root.querySelector(
          `[data-measurement-shape-image="${imageKey}"]`
        );

        /*
         * Preload every image URL stored on the <img> element so switching
         * between states feels instant.
         */
        if (image) {
          Object.entries(image.dataset).forEach(([key, value]) => {
            if (!key.endsWith('Src') || !value) return;

            const preload = new Image();
            preload.src = value;
          });
        }

        inputs.forEach((input) => {
          input.addEventListener('change', () => {
            if (!input.checked) return;

            setImageForValue(imageKey, input.value);
          });
        });

        /*
         * Initial state:
         * - if a radio is already checked, display its matching image
         * - otherwise keep the default image
         */
        const checked = inputs.find((input) => input.checked);

        if (checked) {
          setImageForValue(imageKey, checked.value);
        } else {
          setImageForValue(imageKey, '');
        }
      });

      /*
       * Expose this helper so restoreProfile() or future code can refresh
       * the visual after changing a radio programmatically.
       */
      this.setBodyShapeImage = setImageForValue;
    }

    getProfile() {
      const data = new FormData(this.form);

      const profile = Object.fromEntries(data.entries());

      profile.product_id = this.productId;
      profile.product_handle = this.productHandle;
      profile.guide_type = this.guideType;
      profile.saved_at = new Date().toISOString();

      // Canonical metric values. The wheel internally always stores cm / kg.
      profile.height_cm = Number(profile.height || 0);
      profile.weight_kg = Number(profile.weight || 0);

      return profile;
    }

    saveProfile() {
      if (!this.form) return;

      try {
        localStorage.setItem(this.storageKey, JSON.stringify(this.getProfile()));
      } catch (_) {
        // Browsers can block localStorage in private/restricted contexts.
      }
    }

    restoreProfile() {
      let profile = null;

      try {
        profile = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
      } catch (_) {
        profile = null;
      }

      if (!profile || typeof profile !== 'object') return;

      const nameInput = this.form?.querySelector('input[name="name"]');
      if (nameInput && profile.name) {
        nameInput.value = profile.name;
      }

      const favoriteInput = this.form?.querySelector('input[name="favorite_section"]');
      if (favoriteInput && profile.favorite_section) {
        favoriteInput.value = profile.favorite_section;

        const dropdown = favoriteInput.closest('[data-measurement-dropdown]');
        const display = dropdown?.querySelector('[data-measurement-dropdown-value]');

        if (display) display.textContent = profile.favorite_section;
      }

      ['birth_year', 'height', 'weight'].forEach((name) => {
        const wheel = this.root.querySelector(`[data-wheel-name="${name}"]`);

        if (wheel && profile[name] !== undefined && typeof wheel._setValue === 'function') {
          wheel._setValue(Number(profile[name]));
        }
      });

      ['height_unit', 'weight_unit'].forEach((name) => {
        const input = this.form?.querySelector(`input[name="${name}"]`);
        const switcher = input?.closest('[data-unit-switcher]');

        if (!input || !switcher || !profile[name]) return;

        input.value = profile[name];

        switcher.querySelectorAll('[data-unit-value]').forEach((button) => {
          button.classList.toggle('is-active', button.dataset.unitValue === profile[name]);
        });

        const wheel = switcher
          .closest('.measurement-recommender__dual-wheel')
          ?.querySelector('[data-measurement-wheel]');

        this.refreshWheel(wheel);
      });

      const fitRange = this.root.querySelector('[data-measurement-fit-range]');
      const fitInput = this.root.querySelector('[data-measurement-fit-input]');

      if (fitRange && fitInput && profile.fit_preference) {
        const fitMap = { Fitted: 0, Normal: 1, Loose: 2 };
        const index = fitMap[profile.fit_preference] ?? 1;

        fitRange.value = String(index);
        fitInput.value = profile.fit_preference;

        this.root.querySelectorAll('[data-fit-value]').forEach((button) => {
          button.classList.toggle('is-active', Number(button.dataset.fitValue) === index);
        });
      }

      [
        'abdomen_shape',
        'chest_shape',
        'waist_shape',
        'hip_shape'
      ].forEach((name) => {
        const value = profile[name];

        if (!value) return;

        const radio = this.form?.querySelector(
          `input[name="${name}"][value="${CSS.escape(value)}"]`
        );

        if (radio) {
          radio.checked = true;
          dispatchNativeChange(radio);
        }
      });
    }

    normalizeChart() {
      const chart = this.sizeChart;

      if (!chart || typeof chart !== 'object') return null;

      if (chart[this.guideType] && typeof chart[this.guideType] === 'object') {
        return chart[this.guideType];
      }

      return chart;
    }

    getRecommendationRules() {
      const chart = this.normalizeChart();

      if (!chart || typeof chart !== 'object') return null;

      return (
        chart.recommendation_rules ||
        chart.recommendationRules ||
        chart.rules ||
        null
      );
    }

    calculateRecommendedSize(profile) {
      /*
       * FORM THE LABEL BODY-SHAPE SIZE RULES
       * ------------------------------------
       * Based on the supplied size recommendation table:
       *
       * XS: Waist Slim / Hip Narrow / Abdomen Flat / Chest Narrow
       * S:  Waist Slim / Hip Medium / Abdomen Flat / Chest Medium
       * M:  Waist Regular / Hip Medium / Abdomen Medium / Chest Medium
       * L:  Waist Full / Hip Wide / Abdomen Bulging / Chest Wide
       * XL: No separate body-shape row was supplied. XL is reached when
       *     the base body-shape match is L and Fit Preference is Loose.
       *
       * The active product guide decides which two body-shape answers matter:
       * - jeans_trousers => waist + hip
       * - tops_jackets  => abdomen + chest
       *
       * Fit preference shifts the matched size:
       * - Fitted => one size smaller
       * - Normal => keep the matched size
       * - Loose  => one size larger
       *
       * If the user skips all body-shape questions, the old metric rules
       * from custom.size_chart_data are still used as a fallback when present.
       */

      const orderedSizes = ['XS', 'S', 'M', 'L', 'XL'];

      const bodyShapeRules = {
        XS: {
          waist_shape: 'slim',
          hip_shape: 'narrow',
          abdomen_shape: 'flat',
          chest_shape: 'narrow'
        },
        S: {
          waist_shape: 'slim',
          hip_shape: 'medium',
          abdomen_shape: 'flat',
          chest_shape: 'medium'
        },
        M: {
          waist_shape: 'regular',
          hip_shape: 'medium',
          abdomen_shape: 'medium',
          chest_shape: 'medium'
        },
        L: {
          waist_shape: 'full',
          hip_shape: 'wide',
          abdomen_shape: 'bulging',
          chest_shape: 'wide'
        }
      };

      const relevantFields =
        this.guideType === 'jeans_trousers'
          ? ['waist_shape', 'hip_shape']
          : ['abdomen_shape', 'chest_shape'];

      const answeredFields = relevantFields.filter((field) => {
        return String(profile[field] || '').trim() !== '';
      });

      /*
       * Primary engine: body shape.
       */
      if (answeredFields.length) {
        const scoredSizes = orderedSizes.map((size) => {
          let score = 0;

          answeredFields.forEach((field) => {
            const actual = String(profile[field] || '').trim().toLowerCase();
            const expected = String(bodyShapeRules[size]?.[field] || '').trim().toLowerCase();

            if (actual && expected && actual === expected) {
              score += 1;
            }
          });

          return { size, score };
        });

        const maxScore = Math.max(...scoredSizes.map((item) => item.score));

        if (maxScore > 0) {
          let candidates = scoredSizes
            .filter((item) => item.score === maxScore)
            .map((item) => item.size);

          const fit = String(profile.fit_preference || 'Normal').trim().toLowerCase();

          /*
           * When one optional body-shape answer is skipped, ties are possible.
           * Use the fit preference as the tie-breaker.
           */
          let baseSize;

          if (fit === 'fitted') {
            baseSize = candidates.reduce((best, size) => {
              return orderedSizes.indexOf(size) < orderedSizes.indexOf(best) ? size : best;
            }, candidates[0]);
          } else if (fit === 'loose') {
            baseSize = candidates.reduce((best, size) => {
              return orderedSizes.indexOf(size) > orderedSizes.indexOf(best) ? size : best;
            }, candidates[0]);
          } else {
            /*
             * For NORMAL, prefer the candidate closest to the middle of the
             * available size range. With an S/M tie this resolves to M.
             */
            const normalTargetIndex = 2; // M

            baseSize = candidates.reduce((best, size) => {
              const bestDistance = Math.abs(orderedSizes.indexOf(best) - normalTargetIndex);
              const sizeDistance = Math.abs(orderedSizes.indexOf(size) - normalTargetIndex);

              return sizeDistance < bestDistance ? size : best;
            }, candidates[0]);
          }

          let recommendedIndex = orderedSizes.indexOf(baseSize);

          /*
           * Fit preference intentionally adjusts the final recommendation by
           * one size, while staying inside XS-XL.
           * XL is therefore only produced from an L base match + Loose fit,
           * because the supplied chart does not define a separate XL body shape.
           */
          if (fit === 'fitted') {
            recommendedIndex = Math.max(0, recommendedIndex - 1);
          } else if (fit === 'loose') {
            recommendedIndex = Math.min(orderedSizes.length - 1, recommendedIndex + 1);
          }

          const recommended = orderedSizes[recommendedIndex];

          return {
            size: recommended,
            reason: `Based on your body shape and ${profile.fit_preference || 'Normal'} fit preference.`
          };
        }
      }

      /*
       * Fallback engine: existing metric rules from custom.size_chart_data.
       * This keeps backwards compatibility for products that already have
       * height/weight recommendation rules configured.
       */
      const chart = this.normalizeChart();
      const rules = this.getRecommendationRules();

      if (!chart || !rules || typeof rules !== 'object') {
        return {
          size: null,
          reason: 'Choose at least one body-shape option to see your recommended size.'
        };
      }

      const metricOrderedSizes =
        Array.isArray(chart.size_order) && chart.size_order.length
          ? chart.size_order.map(String)
          : ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];

      const height = Number(profile.height_cm);
      const weight = Number(profile.weight_kg);

      const matches = [];

      Object.entries(rules).forEach(([size, rule]) => {
        if (!rule || typeof rule !== 'object') return;

        let score = 0;
        let checks = 0;
        let hardFail = false;

        const scoreRange = (actual, range) => {
          if (!Array.isArray(range) || range.length < 2 || !Number.isFinite(actual)) {
            return;
          }

          const min = Number(range[0]);
          const max = Number(range[1]);

          if (!Number.isFinite(min) || !Number.isFinite(max)) return;

          checks += 1;

          if (actual >= min && actual <= max) {
            const center = (min + max) / 2;
            const half = Math.max((max - min) / 2, 1);
            score += 1 - Math.min(Math.abs(actual - center) / half, 1) * 0.35;
          } else {
            hardFail = true;
          }
        };

        scoreRange(height, rule.height_cm);
        scoreRange(weight, rule.weight_kg);

        if (!hardFail && checks > 0) {
          matches.push({
            size,
            score: score / checks
          });
        }
      });

      if (!matches.length) {
        return {
          size: null,
          reason: 'Choose at least one body-shape option to see your recommended size.'
        };
      }

      matches.sort((a, b) => b.score - a.score);

      let recommended = matches[0].size;
      const fit = String(profile.fit_preference || 'Normal').toLowerCase();
      const currentIndex = metricOrderedSizes.indexOf(recommended);

      if (currentIndex >= 0) {
        if (fit === 'loose' && currentIndex < metricOrderedSizes.length - 1) {
          recommended = metricOrderedSizes[currentIndex + 1];
        }

        if (fit === 'fitted' && currentIndex > 0) {
          recommended = metricOrderedSizes[currentIndex - 1];
        }
      }

      return {
        size: recommended,
        reason: `Based on your saved measurements and ${profile.fit_preference || 'Normal'} fit preference.`
      };
    }

    updateRecommendation() {
      const profile = this.getProfile();
      const result = this.calculateRecommendedSize(profile);

      const sizeElement = this.root.querySelector('[data-measurement-recommended-size]');
      const noteElement = this.root.querySelector('[data-measurement-result-note]');
      const metaElement = this.root.querySelector('[data-measurement-result-meta]');
      const applyButton = this.root.querySelector('[data-measurement-apply-size]');
      const statusElement = this.root.querySelector('[data-measurement-result-status]');

      this.recommendedSize = result.size || null;

      if (sizeElement) {
        sizeElement.textContent = this.recommendedSize || '--';
      }

      if (noteElement) {
        noteElement.textContent = result.reason || '';
      }

      if (metaElement) {
        const guideLabel =
          this.guideType === 'jeans_trousers'
            ? 'Jeans & Trousers'
            : this.guideType === 'tops_jackets'
              ? 'Tops & Jackets'
              : '';

        metaElement.textContent = guideLabel ? `Guide: ${guideLabel}` : '';
        metaElement.hidden = !guideLabel;
      }

      if (applyButton) {
        applyButton.disabled = !this.recommendedSize;
      }

      if (statusElement) {
        statusElement.hidden = true;
        statusElement.textContent = '';
      }
    }

    applyRecommendedSize() {
      if (!this.recommendedSize) return;

      const size = this.recommendedSize.trim();
      const status = this.root.querySelector('[data-measurement-result-status]');

      const applied = this.selectSizeOnProductPage(size);

      if (status) {
        status.hidden = false;
        status.textContent = applied
          ? `Size ${size} has been selected.`
          : `Size ${size} is not currently available on this product.`;
      }

      if (applied) {
        this.saveProfile();

        setTimeout(() => {
          this.close();
        }, 500);
      }
    }

    selectSizeOnProductPage(size) {
      const normalized = size.toLowerCase();

      // 1) Shopify radio / button variant pickers.
      const radios = [...document.querySelectorAll('input[type="radio"]')];

      const radio = radios.find((input) => {
        const value = String(input.value || '').trim().toLowerCase();

        if (value !== normalized) return false;

        const name = String(input.name || '').toLowerCase();
        const id = String(input.id || '').toLowerCase();

        return (
          name.includes('option') ||
          name.includes('size') ||
          id.includes('size') ||
          input.closest('variant-picker, [data-variant-picker], .variant-picker')
        );
      });

      if (radio && !radio.disabled) {
        radio.checked = true;
        radio.click();
        dispatchNativeChange(radio);
        return true;
      }

      // 2) Select-based variant pickers.
      const selects = [...document.querySelectorAll('select')];

      for (const select of selects) {
        const option = [...select.options].find(
          (item) => String(item.value || item.textContent).trim().toLowerCase() === normalized
        );

        if (!option || option.disabled) continue;

        select.value = option.value;
        dispatchNativeChange(select);
        return true;
      }

      // 3) Fallback: clickable labels/buttons carrying the size as text/value.
      const candidates = [
        ...document.querySelectorAll(
          '[data-option-value], [data-value], .variant-option__button-label, .variant-picker__label'
        )
      ];

      const candidate = candidates.find((element) => {
        const value =
          element.getAttribute('data-option-value') ||
          element.getAttribute('data-value') ||
          element.textContent ||
          '';

        return String(value).trim().toLowerCase() === normalized;
      });

      if (candidate && !candidate.hasAttribute('disabled')) {
        candidate.click();
        return true;
      }

      return false;
    }

    trapFocus(event) {
      const focusable = [
        ...this.panel.querySelectorAll(
          'button:not([disabled]):not([hidden]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
        )
      ].filter((element) => {
        return !element.closest('[hidden]') && element.offsetParent !== null;
      });

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const instances = new Map();

  const openFromTrigger = (trigger) => {
    if (!trigger) return;

    init();

    const instance = findInstanceForTrigger(trigger);
    instance?.open(trigger);
  };

  const bindOpenTriggers = () => {
    document.querySelectorAll(OPEN_SELECTOR).forEach((trigger) => {
      if (trigger.dataset.measurementOpenBound === 'true') return;

      trigger.dataset.measurementOpenBound = 'true';

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openFromTrigger(trigger);
      });
    });
  };

  const init = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (instances.has(root)) return;

      instances.set(root, new MeasurementRecommender(root));
    });

    bindOpenTriggers();
  };

  const findInstanceForTrigger = (trigger) => {
    const section =
      trigger.closest('.shopify-section') ||
      trigger.closest('[id^="shopify-section-"]') ||
      trigger.closest('section');

    if (section) {
      const root = section.querySelector(ROOT_SELECTOR);
      if (root && instances.has(root)) {
        return instances.get(root);
      }
    }

    const firstRoot = document.querySelector(ROOT_SELECTOR);
    return firstRoot ? instances.get(firstRoot) : null;
  };

  /*
   * Capture-phase fallback.
   * Some Shopify theme components stop click propagation before it reaches
   * document in the normal bubble phase. Listening in capture phase makes
   * the Measurement Recommender trigger reliable even inside accordions,
   * product blocks, and theme-editor wrappers.
   */
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest?.(OPEN_SELECTOR);
      if (!trigger) return;

      event.preventDefault();
      openFromTrigger(trigger);
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor / section reload support.
  document.addEventListener('shopify:section:load', init);
})();
